import {QUERY_KEYS} from '@/queryClient'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {
    HttpGetUsersByGroupResponse,
    httpGetUsersByGroup,
    HttpGetUsersByGroupOptions
} from '@/features/users/http/users.http'
import {z} from 'zod'
import {UserSpecialization} from '../types'
import {useInputSearch} from '@/hooks/useInputSearch'
import {PaginationResponse} from '@/types'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'

export const HttpGetSpecializationsResponse = z.object({
    data: z.array(UserSpecialization),
    meta: PaginationResponse
})
export type HttpGetSpecializationsResponse = z.infer<typeof HttpGetSpecializationsResponse>

export const useGetUserByGroup = (
    params: Omit<HttpGetUsersByGroupOptions['params'], 'page'>,
    options?: Pick<UseInfiniteQueryOptions<HttpGetUsersByGroupResponse>, 'enabled'>
) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.USER_BY_GROUP, searchValue, params],
        queryFn: ({pageParam = 1}) =>
            httpGetUsersByGroup({
                params: {
                    group: params.group,
                    responseType: params.responseType,
                    search: searchValue ?? params.search,
                    limit: params.limit,
                    page: pageParam
                }
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : [],
        searchValue,
        onResetSearch,
        onSearch
    }
}
